import { Helmet } from "react-helmet-async";
import { HowItWorksSection } from "components/how-it-works-section";
import { FeaturesSection } from "components/features-section";
import { CTASection } from "components/cta-section";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/accordion";
import { CheckCircle, AlertTriangle, HelpCircle } from "lucide-react";
import { howItWorksFaqs } from "content/faqContent";
import { FeaturedCampaigns } from "components/featured-campaigns";

const investorRequirements = [
"Be at least 18 years old",
"Have a valid government-issued ID",
"Provide your Social Insurance Number (SIN) for tax reporting purposes under the Income Tax Act (Canada)",
"Have a valid Canadian bank account or approved Canadian payment method",
"Complete the investor questionnaire"];


const investmentLimitDescription =
  "Maximum investment per investor per offering: $2,500. This limit applies to every investor on the Platform regardless of investor status, in accordance with NI 45-110 section 5(1)(p)(i). The Platform enforces this limit automatically. It is your responsibility to ensure you do not exceed this limit.";


const faqs = [
{
  question: "What is equity crowdfunding?",
  answer: "Equity crowdfunding allows private companies to raise capital from individual investors in exchange for shares in the company. Unlike traditional investments, equity crowdfunding enables anyone to invest in early-stage companies with relatively small amounts of money."
},
{
  question: "How much can I invest?",
  answer: "Your investment limit depends on your annual income and net worth. Generally, if your income or net worth is less than $124,000, you can invest up to 5% of the lesser amount. If both exceed $124,000, you can invest up to 10%, with a maximum of $124,000 per year across all equity crowdfunding platforms."
},
{
  question: "When will I see a return on my investment?",
  answer: "Returns on equity crowdfunding investments are not guaranteed and typically occur during a liquidity event such as an IPO, acquisition, or company buyback. These events may take several years to occur, and there's always a risk that they may never happen."
},
{
  question: "Can I sell my shares?",
  answer: "Shares purchased through equity crowdfunding are generally illiquid, meaning they cannot be easily sold or traded. You typically need to hold your shares until a liquidity event occurs. Some platforms offer secondary markets, but these are limited."
},
{
  question: "What happens if the company fails?",
  answer: "If a company fails, you may lose your entire investment. Private companies, especially startups, have high failure rates. You should only invest money you can afford to lose completely."
},
{
  question: "How are companies vetted?",
  answer: "We conduct Internal Review on every company before they can list on our platform. This includes reviewing financial statements, business plans, legal compliance, and team backgrounds. However, this does not guarantee success or eliminate risk."
}];


export default function HowItWorksPage() {
  return (
    <>
    <Helmet>
      <title>How It Works | Fortify</title>
      <meta name="description" content="Learn how equity crowdfunding works and how you can start investing in private companies." />
    </Helmet>
    {/* Hero Section */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              How Equity Crowdfunding Works
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Learn everything you need to know about investing in private companies through our platform.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <HowItWorksSection />

        {/* Requirements Section */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
            <div className="flex flex-wrap items-stretch justify-center gap-8">
              <Card className="d-none w-full max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent" />
                    Investor Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    To invest on Fortify, you must meet the following requirements:
                  </p>
                  <ul className="space-y-3">
                    {investorRequirements.map((req, index) =>
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                        <span className="text-sm">{req}</span>
                      </li>
                      )}
                  </ul>
                </CardContent>
              </Card>

              <Card className="w-full max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                    Investment Limits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {investmentLimitDescription}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        <FeaturedCampaigns cardsOnly />
        {/* Features */}
        {/* <FeaturesSection /> */}

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <HelpCircle className="h-12 w-12 mx-auto text-accent mb-4" />
                <h2 className="text-3xl font-bold tracking-tight">
                  Frequently Asked Questions
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Get answers to common questions about equity crowdfunding.
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full">
                {howItWorksFaqs.map((faq, index) =>
                  <AccordionItem key={faq.question} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                  )}
              </Accordion>
            </div>
          </div>
        </section>

    {/* CTA */}
    <CTASection />
    </>);

}